import { useState, useEffect, useCallback } from 'react';
import { api, MarketAsset, MarketListing } from '../services/api';
import { toast } from '../hooks/use-toast';

// 出品可能な種別（バックエンドの ASSET_KINDS と一致させること）
const KINDS: { value: string; label: string }[] = [
    { value: 'background', label: '背景' },
    { value: 'skin', label: '内装スキン' },
    { value: 'avatar', label: '立ち絵' },
    { value: 'bgm', label: 'BGM' },
    { value: 'se', label: 'SE' },
    { value: 'brush', label: 'ブラシ' },
    { value: 'refpack', label: '参照パック' },
    { value: 'script_theme', label: '台本装丁' },
    { value: 'stage_preset', label: '演出プリセット' },
];
const kindLabel = (v: string) => KINDS.find(k => k.value === v)?.label ?? v;

type Tab = 'browse' | 'library' | 'publish' | 'shop';

export function Market() {
    const [tab, setTab] = useState<Tab>('browse');
    const [paidEnabled, setPaidEnabled] = useState(false);

    return (
        <div className="max-w-5xl mx-auto px-4 py-6">
            <header className="mb-5">
                <h1 className="text-2xl font-extrabold text-ink">プリセット・マーケット</h1>
                <p className="text-sm text-gray-500 mt-1">
                    三役が作った背景・立ち絵・BGM・ブラシ・台本装丁を共有。飾って、作り手を応援。
                </p>
                {!paidEnabled && (
                    <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm px-4 py-2">
                        現在すべて<strong>無料配布中</strong>です。（有料販売は準備が整い次第、順次公開予定）
                    </div>
                )}
            </header>

            <nav className="flex gap-2 border-b border-cream-2 mb-5 text-sm font-bold">
                {([['browse', 'さがす'], ['library', 'マイ入手'], ['publish', '出品する'], ['shop', 'ショップ']] as [Tab, string][]).map(([t, label]) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`px-4 py-2 -mb-px border-b-2 transition-colors ${tab === t ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-ink'}`}
                    >
                        {label}
                    </button>
                ))}
            </nav>

            {tab === 'browse' && <BrowseTab onPaidFlag={setPaidEnabled} />}
            {tab === 'library' && <LibraryTab />}
            {tab === 'publish' && <PublishTab paidEnabled={paidEnabled} onDone={() => setTab('shop')} />}
            {tab === 'shop' && <ShopTab />}
        </div>
    );
}

function PriceTag({ asset }: { asset: MarketAsset }) {
    if (asset.isFree) return <span className="text-sm font-bold text-gray-500">無料</span>;
    return <span className="text-sm font-extrabold text-emerald-600">{asset.priceAdp} ADP</span>;
}

function AssetCard({ asset, onAcquire, owned }: { asset: MarketAsset; onAcquire?: (a: MarketAsset) => void; owned?: boolean }) {
    return (
        <div className="rounded-xl border border-cream-2 bg-white overflow-hidden flex flex-col">
            <div className="h-28 bg-cream flex items-end p-2" style={asset.coverUrl ? { backgroundImage: `url(${asset.coverUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                <span className="text-[10px] font-bold bg-black/40 text-white rounded px-2 py-0.5">{kindLabel(asset.kind)}</span>
            </div>
            <div className="p-3 flex flex-col gap-1 flex-1">
                <div className="text-sm font-bold text-ink line-clamp-1">{asset.title}</div>
                <div className="text-[11px] text-gray-400">by {asset.sellerName ?? '匿名'}</div>
                <div className="flex items-center justify-between mt-2">
                    <PriceTag asset={asset} />
                    <span className="text-[11px] text-amber-500">★{asset.ratingAvg || '-'} ・ {asset.acquireCount}入手</span>
                </div>
                {onAcquire && (
                    <button
                        disabled={owned}
                        onClick={() => onAcquire(asset)}
                        className={`mt-2 w-full rounded-lg py-1.5 text-sm font-bold transition-colors ${owned ? 'bg-cream text-gray-400 cursor-default' : 'bg-primary text-white hover:opacity-90'}`}
                    >
                        {owned ? '入手済み' : asset.isFree ? '入手（無料）' : `購入（${asset.priceAdp} ADP）`}
                    </button>
                )}
            </div>
        </div>
    );
}

function BrowseTab({ onPaidFlag }: { onPaidFlag: (b: boolean) => void }) {
    const [assets, setAssets] = useState<MarketAsset[]>([]);
    const [kind, setKind] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [ownedIds, setOwnedIds] = useState<Set<string>>(new Set());

    const load = useCallback(async () => {
        setLoading(true);
        const res = await api.listMarketAssets({ kind: kind || undefined, sort: 'new', limit: 60 });
        setAssets(res.assets);
        onPaidFlag(res.paidEnabled);
        setLoading(false);
    }, [kind, onPaidFlag]);

    useEffect(() => { load(); }, [load]);

    const acquire = async (a: MarketAsset) => {
        const r = await api.acquireMarketAsset(a.id);
        if (r.success) {
            setOwnedIds(prev => new Set(prev).add(a.id));
            toast({ title: r.data?.alreadyOwned ? '入手済みです' : '入手しました', description: a.title });
        } else if (r.code === 'UNAUTHORIZED') {
            toast({ title: 'ログインが必要です', description: '入手にはログインしてください', variant: 'destructive' });
        } else if (r.code === 'INSUFFICIENT_BALANCE') {
            toast({ title: 'ADPが不足しています', description: r.message, variant: 'destructive' });
        } else {
            toast({ title: '入手に失敗しました', description: r.message, variant: 'destructive' });
        }
    };

    return (
        <div>
            <div className="flex gap-2 flex-wrap mb-4">
                <button onClick={() => setKind('')} className={`text-xs px-3 py-1 rounded-full border ${kind === '' ? 'bg-primary text-white border-primary' : 'border-cream-2 text-gray-500'}`}>すべて</button>
                {KINDS.map(k => (
                    <button key={k.value} onClick={() => setKind(k.value)} className={`text-xs px-3 py-1 rounded-full border ${kind === k.value ? 'bg-primary text-white border-primary' : 'border-cream-2 text-gray-500'}`}>{k.label}</button>
                ))}
            </div>
            {loading ? (
                <p className="text-sm text-gray-400 py-10 text-center">読み込み中…</p>
            ) : assets.length === 0 ? (
                <p className="text-sm text-gray-400 py-10 text-center">まだアセットがありません。最初の出品者になりましょう。</p>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {assets.map(a => <AssetCard key={a.id} asset={a} onAcquire={acquire} owned={ownedIds.has(a.id)} />)}
                </div>
            )}
        </div>
    );
}

function LibraryTab() {
    const [assets, setAssets] = useState<MarketAsset[]>([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => { (async () => { setAssets(await api.getMarketLibrary()); setLoading(false); })(); }, []);
    if (loading) return <p className="text-sm text-gray-400 py-10 text-center">読み込み中…</p>;
    if (assets.length === 0) return <p className="text-sm text-gray-400 py-10 text-center">入手したアセットはまだありません。</p>;
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {assets.map(a => <AssetCard key={a.id} asset={a} owned />)}
        </div>
    );
}

function PublishTab({ paidEnabled, onDone }: { paidEnabled: boolean; onDone: () => void }) {
    const [kind, setKind] = useState('background');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priceAdp, setPriceAdp] = useState(0);
    const [coverUrl, setCoverUrl] = useState<string | null>(null);
    const [assetUrl, setAssetUrl] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const upload = async (file: File, target: 'cover' | 'asset') => {
        setUploading(true);
        const r = await api.uploadImage(file);
        setUploading(false);
        if (r.success && r.data) {
            if (target === 'cover') setCoverUrl(r.data.url);
            else setAssetUrl(r.data.url);
        } else {
            toast({ title: 'アップロードに失敗しました', variant: 'destructive' });
        }
    };

    const submit = async () => {
        if (!title || !assetUrl) {
            toast({ title: 'タイトルとアセット画像は必須です', variant: 'destructive' });
            return;
        }
        setSubmitting(true);
        const r = await api.publishMarketAsset({
            kind, title, description,
            coverUrl: coverUrl ?? assetUrl,
            assetUrl,
            priceAdp: paidEnabled ? priceAdp : 0,
        });
        setSubmitting(false);
        if (r.success) {
            toast({ title: '出品しました', description: '審査後に公開されます' });
            onDone();
        } else if (r.code === 'UNAUTHORIZED') {
            toast({ title: 'ログインが必要です', variant: 'destructive' });
        } else {
            toast({ title: '出品に失敗しました', description: r.message, variant: 'destructive' });
        }
    };

    return (
        <div className="max-w-lg space-y-4">
            <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">種別</label>
                <select value={kind} onChange={e => setKind(e.target.value)} className="w-full rounded-lg border border-cream-2 px-3 py-2 text-sm">
                    {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
            </div>
            <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">タイトル</label>
                <input value={title} onChange={e => setTitle(e.target.value)} maxLength={120} className="w-full rounded-lg border border-cream-2 px-3 py-2 text-sm" placeholder="夕暮れの屋上パック" />
            </div>
            <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">説明</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full rounded-lg border border-cream-2 px-3 py-2 text-sm" />
            </div>
            <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">アセット画像（必須）</label>
                <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && upload(e.target.files[0], 'asset')} className="text-xs" />
                {assetUrl && <img src={assetUrl} alt="asset" className="mt-2 h-24 rounded-lg object-cover border border-cream-2" />}
            </div>
            <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">カバー画像（任意）</label>
                <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && upload(e.target.files[0], 'cover')} className="text-xs" />
                {coverUrl && <img src={coverUrl} alt="cover" className="mt-2 h-24 rounded-lg object-cover border border-cream-2" />}
            </div>
            <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">
                    価格（ADP）{!paidEnabled && <span className="text-gray-400 font-normal">— 現在は無料配布のみ（有料販売は準備中）</span>}
                </label>
                <input
                    type="number" min={0} value={priceAdp}
                    disabled={!paidEnabled}
                    onChange={e => setPriceAdp(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="w-40 rounded-lg border border-cream-2 px-3 py-2 text-sm disabled:bg-cream disabled:text-gray-400"
                />
            </div>
            <button
                onClick={submit}
                disabled={uploading || submitting}
                className="rounded-lg bg-primary text-white font-bold px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50"
            >
                {submitting ? '送信中…' : uploading ? 'アップロード中…' : '出品する（審査へ）'}
            </button>
        </div>
    );
}

function ShopTab() {
    const [data, setData] = useState<{ paidEnabled: boolean; revenueAdp: number; soldCount: number; listings: MarketListing[] } | null>(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => { (async () => { setData(await api.getMarketShop()); setLoading(false); })(); }, []);
    if (loading) return <p className="text-sm text-gray-400 py-10 text-center">読み込み中…</p>;
    if (!data) return <p className="text-sm text-gray-400 py-10 text-center">ショップ情報を取得できませんでした（ログインが必要です）。</p>;
    return (
        <div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                <div className="rounded-xl border border-cream-2 bg-white p-4">
                    <div className="text-xs text-gray-400">売上（ADP）</div>
                    <div className="text-2xl font-extrabold text-emerald-600">{data.revenueAdp}</div>
                    {!data.paidEnabled && <div className="text-[10px] text-gray-400 mt-1">無料配布中のため売上は0です</div>}
                </div>
                <div className="rounded-xl border border-cream-2 bg-white p-4">
                    <div className="text-xs text-gray-400">有料販売数</div>
                    <div className="text-2xl font-extrabold text-ink">{data.soldCount}</div>
                </div>
                <div className="rounded-xl border border-cream-2 bg-white p-4">
                    <div className="text-xs text-gray-400">出品数</div>
                    <div className="text-2xl font-extrabold text-ink">{data.listings.length}</div>
                </div>
            </div>
            {data.listings.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">まだ出品がありません。「出品する」から登録できます。</p>
            ) : (
                <div className="space-y-2">
                    {data.listings.map(l => (
                        <div key={l.id} className="flex items-center gap-3 rounded-lg border border-cream-2 bg-white p-3">
                            <div className="w-12 h-12 rounded bg-cream shrink-0" style={l.coverUrl ? { backgroundImage: `url(${l.coverUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold text-ink line-clamp-1">{l.title}</div>
                                <div className="text-[11px] text-gray-400">{kindLabel(l.kind)} ・ {l.acquireCount}入手</div>
                            </div>
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${l.status === 'PUBLISHED' ? 'bg-emerald-100 text-emerald-700' : l.status === 'PENDING' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                                {l.status === 'PUBLISHED' ? '公開中' : l.status === 'PENDING' ? '審査中' : l.status === 'REJECTED' ? '却下' : '非公開'}
                            </span>
                            <span className="text-xs font-bold text-gray-500 w-16 text-right">{data.paidEnabled ? `${l.priceAdp} ADP` : '無料'}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
